import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";

export const getOneActionKey = "getOneAction" as const;

export type Options = {
    actionId: string | undefined;
};
export const useGetOneAction = (options: Options) => {
    const { actionId } = options;

    const query = useQuery({
        queryKey: [getOneActionKey, actionId],
        cacheTime: 0,
        enabled: actionId !== undefined,
        queryFn: async () => {
            const url = `/api/actions/${actionId}`;
            return await sureApiCall.get(url);
        },
        select: (response) => {
            const action = response.data;
            const data = zodParseWithLog(action, ActionsSchema, getOneActionKey);
            return data;
        },
    });

    return {
        action: query.data,
        ...query,
    };
};

const FieldSchema = z.object({
    type: z.union([
        z.literal("group"),
        z.literal("text"),
        z.literal("select"),
        z.literal("radio"),
        z.literal("checkbox"),
        z.literal("date"),
        z.literal("daterange"),
    ]),
});
export type FieldPropertyType = z.infer<typeof FieldSchema>;

const DataListOptionSchema = z.object({
    id: z.number(),
    value: z.string(),
    label: z.string(),
    position: z.number(),
    groupOption: z.string().optional(),
});

export type DataListOption = z.infer<typeof DataListOptionSchema>;

const DataListSchema = z.object({
    options: DataListOptionSchema.array(),
});

const UnitSchema = z.object({
    id: z.number(),
    title: z.string(),
    abbreviation: z.string(),
});
export type Unit = z.infer<typeof UnitSchema>;

export type SsqPropertyAction = z.infer<typeof SsqPropertiesSchema>;
export type SsqPropertyActionHead = z.infer<typeof SsqPropertiesSchema>;
const SsqPropertiesSchemaHead = z.object({
    id: z.number(),
    title: z.string().optional(),
    name: z.string().optional(),
    field: FieldSchema.optional(),
    unit: UnitSchema.optional(),
    position: z.number().optional(),
    multivalue: z.boolean().optional(),
    dataList: DataListSchema.optional(),
    defaultValue: z.string().optional(),
    hidden: z.boolean().optional(),
    hint: z.string().optional(),
    readonly: z.boolean().optional(),
    genericValue: z.string().optional(),

    // Additional Properties field only for action call
    actionChildrenIds: z.number().array().optional(),
});
export const SsqPropertiesSchema = SsqPropertiesSchemaHead.extend({
    // The optional here is to handle a level of nested.
    children: SsqPropertiesSchemaHead.array().optional(),
});
export type SsqProperty = z.infer<typeof SsqPropertiesSchema>;

const SureCsrThemeSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    slug: z.string(),
    title: z.string(),
    description: z.string().optional(),
});
export type SureCsrTheme = z.infer<typeof SureCsrThemeSchema>;

export const ActionsSchema = z.object({
    id: z.number(),
    title: z.string(),
    slug: z.string(),
    csrThemes: SureCsrThemeSchema.array(),
    ssqProperties: SsqPropertiesSchema.array(),
});
export type ActionsType = z.infer<typeof ActionsSchema>;
